import { useCallback, useMemo } from 'react';
import type {
  BookingValidationErrors,
  UpdateResult,
} from '../../../services/update-form/updateBookingReducer';
import {
  type BookingFormState,
  type FormInstanceId,
  getEditedFormState,
  getInitialFormState,
  getUpdateFormState,
} from '../../../services/update-form/updateFormReducer';
import { isEqualFormStates } from '../../../services/update-form/utils/isEqualFormStates';
import type { StoreState } from '../../../store/storeState';
import type { BasicBooking } from '../../graphql/fragments/gql/BasicBooking';
import { useShallowEqualSelector } from '../../hooks/useShallowEqualSelector';
import {
  getUpdateSubmitInformation,
  type UpdateSubmitInformation,
} from '../utils/getUpdateSubmitInformation';
import { useUpdateSubmit } from './useUpdateSubmit';

export interface UseUpdateFormState {
  booking?: BasicBooking;
  edited: boolean;
  formState?: BookingFormState;
  submit: () => void;
  submitting: boolean;
  updateSubmitInformation: UpdateSubmitInformation;
  validationErrors?: BookingValidationErrors;
}

export interface StateProps {
  booking?: BasicBooking;
  editedFormState?: BookingFormState;
  initialFormState?: BookingFormState;
  updateResult: UpdateResult | null;
  validationErrors: BookingValidationErrors | undefined;
}

export const useUpdateFormState = ({ bookingNo, formId }: FormInstanceId): UseUpdateFormState => {
  const mapState = useCallback(
    (state: StoreState): StateProps => {
      const updateFormState = getUpdateFormState(state, { bookingNo, formId });
      return {
        booking: updateFormState ? updateFormState.booking : undefined,
        editedFormState: getEditedFormState(state, { bookingNo, formId }),
        initialFormState: getInitialFormState(state, { bookingNo, formId }),
        updateResult: updateFormState ? updateFormState.meta.updateResult : null,
        validationErrors: updateFormState ? updateFormState.meta.validationErrors : undefined,
      };
    },
    [bookingNo, formId],
  );

  const { booking, editedFormState, initialFormState, updateResult, validationErrors } =
    useShallowEqualSelector(mapState);

  const { submitting, submit } = useUpdateSubmit({
    bookingNo: bookingNo,
    formId: formId,
  });

  const updateSubmitInformation = useMemo(
    () => getUpdateSubmitInformation(updateResult || {}),
    [updateResult],
  );

  return {
    booking,
    edited: !isEqualFormStates(initialFormState, editedFormState),
    formState: editedFormState,
    submit,
    submitting,
    updateSubmitInformation,
    validationErrors,
  };
};
